import type { EtatCommande } from '@artosera/shared';
import {
  useCommandesBoutique,
  useOeuvresBoutique,
  type CommandePresentee,
} from '../../api/boutique.js';

/** Plafond de l'API (`parPage`) : une seule page suffit pour les commandes à traiter. */
const PAR_PAGE_MAX = 200;

export type EtapeCommande = 'remettre' | 'attendre' | 'terminerRemboursement';

/**
 * Ce que la galerie a à faire d'une commande, d'après son état seul (aucune
 * règle de paiement ici) : `null` quand la commande est close.
 */
export function etapeSuivante(etat: EtatCommande): EtapeCommande | null {
  if (etat === 'payee') return 'remettre';
  if (etat === 'en_paiement') return 'attendre';
  if (etat === 'a_rembourser') return 'terminerRemboursement';
  return null;
}

const MOIS_PARIS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
});

/** Chiffre du mois : somme des commandes payées dont le paiement date du mois courant (heure de Paris). */
export function chiffreDuMois(
  commandes: readonly Pick<CommandePresentee, 'etat' | 'payeeLe' | 'montantCentimes'>[],
  maintenant: Date = new Date(),
): { centimes: number; nombre: number } {
  const mois = MOIS_PARIS.format(maintenant);
  const duMois = commandes.filter(
    (c) => c.etat === 'payee' && c.payeeLe && MOIS_PARIS.format(new Date(c.payeeLe)) === mois,
  );
  return {
    centimes: duMois.reduce((somme, c) => somme + c.montantCentimes, 0),
    nombre: duMois.length,
  };
}

/**
 * Données des indicateurs et de la file « à traiter », tirées des listes
 * existantes de l'API : totaux de pagination et somme faite ici, rien de plus.
 */
export function useSyntheseBoutique(galerie: string) {
  const oeuvresEnVente = useOeuvresBoutique(galerie, { enVente: true, parPage: 1 });
  const payees = useCommandesBoutique(galerie, { etat: 'payee', parPage: PAR_PAGE_MAX });
  const aRembourser = useCommandesBoutique(galerie, {
    etat: 'a_rembourser',
    parPage: PAR_PAGE_MAX,
  });
  const nbPayees = payees.data?.pagination.total;
  const nbARembourser = aRembourser.data?.pagination.total;
  return {
    chargement: oeuvresEnVente.isLoading || payees.isLoading || aRembourser.isLoading,
    nbOeuvresEnVente: oeuvresEnVente.data?.pagination.total,
    nbARembourser,
    nbATraiter:
      nbPayees === undefined || nbARembourser === undefined ? undefined : nbPayees + nbARembourser,
    /** Remboursements d'abord : ce sont eux qui bloquent un acheteur. */
    aTraiter: [...(aRembourser.data?.commandes ?? []), ...(payees.data?.commandes ?? [])],
    mois: payees.data ? chiffreDuMois(payees.data.commandes) : undefined,
    /** Plus de payées que l'API n'en rend d'un coup : le chiffre du mois peut être sous-estimé. */
    moisPartiel: (payees.data?.pagination.pages ?? 1) > 1,
  };
}
